/* eslint-disable @typescript-eslint/no-unused-expressions */
import {
    createClient,
    type ClientOptions,
    ConnectionAckMessage,
} from 'graphql-ws';

import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import {
    pGraphQLWsEvent,
    pWsCloseEvent,
    pData,
    pNetworkError,
    pWsMessage,
    pWebSocket,
} from '../pretty';
import { HeadersOptions, getHeadersProvider } from '../utils/headers-provider';

export type GraphQLWsLinkOptions = HeadersOptions & {
    url: string | (() => string | Promise<string>);
    lazy?: boolean;
    lazyCloseTimeout?: number;
    connectionAckWaitTimeout?: number;
    retryAttempts?: number;
    shouldRetry?: (errOrCloseEvent: unknown) => boolean;
    on?: ClientOptions['on'];
    debugLogs?: boolean;
};

function createListeners(
    on?: ClientOptions['on'],
    debugLogs = false
): ClientOptions['on'] {
    if (!debugLogs) return on;

    const pretty = console.pretty;

    return {
        opened: (socket: unknown) => {
            pretty.debug`${pGraphQLWsEvent('opened')} ${pWebSocket(
                socket as WebSocket
            )}`;
            if (on?.opened) on.opened(socket);
        },
        closed: (closeEvent: unknown) => {
            pretty.debug`${pGraphQLWsEvent('closed')} ${pWsCloseEvent(
                closeEvent as CloseEvent
            )}`;
            if (on?.closed) on.closed(closeEvent);
        },
        connected: (
            socket: unknown,
            payload: ConnectionAckMessage['payload'],
            wasRetry: boolean
        ) => {
            pretty.debug`${pGraphQLWsEvent('connected')} ${pData(
                payload
            )}, wasRetry: ${pData(wasRetry)}`;
            if (on?.connected) on.connected(socket, payload, wasRetry);
        },
        connecting: (isRetry: boolean) => {
            pretty.debug`${pGraphQLWsEvent('connecting')}, isRetry: ${pData(
                isRetry
            )}`;
            if (on?.connecting) on.connecting(isRetry);
        },
        error: (error) => {
            pretty.debug`${pGraphQLWsEvent('error')} ${pNetworkError(error)}`;
            if (on?.error) on.error(error);
        },
        message: (message) => {
            pretty.debug`${pGraphQLWsEvent('message')} ${pWsMessage(message)}`;
            if (on?.message) on.message(message);
        },
        ping: (received, payload) => {
            if (!received) {
                pretty.debug`${pGraphQLWsEvent('send ping')} ${pData(payload)}`;
            }

            if (on?.ping) on.ping(received, payload);
        },
        pong: (received, payload) => {
            if (!received) {
                pretty.debug`${pGraphQLWsEvent('send pong')} ${pData(payload)}`;
            }

            if (on?.pong) on.pong(received, payload);
        },
    };
}

export function makeGraphQLWsLink(options: GraphQLWsLinkOptions) {
    const client = createClient({
        url: options.url,
        connectionParams: getHeadersProvider(options),
        connectionAckWaitTimeout: options.connectionAckWaitTimeout,
        lazy: options.lazy,
        lazyCloseTimeout: options.lazyCloseTimeout,
        retryAttempts: options.retryAttempts,
        shouldRetry: options.shouldRetry,
        on: createListeners(options.on, options.debugLogs ?? false),
    });

    return new GraphQLWsLink(client);
}
